import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ToolShell, NumInput, SelectInput, ErrorBox, ResultGrid, EstimateNote, WhatsAppCTA } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

export default function EnergyBattery() {
  const [form, setForm] = useState({ chemistry: "lithium", voltage: 12, amp_hours: 200, count: 1, age_years: 0, state_of_charge_pct: 100, load_w: "", desired_runtime_h: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const compute = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== "" || ["chemistry", "voltage", "amp_hours", "count"].includes(k)));
      const { data } = await api.post("/energy/battery", payload);
      setResult(data.result);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Battery Health & Runtime Calculator — BNM" description="Estimate your battery bank's usable capacity and runtime. Battery performance varies by chemistry, temperature, age and discharge rate." />
      <ToolShell
        testid="battery-tool"
        eyebrow="Battery Health & Runtime Calculator"
        title="How healthy is your battery bank?"
        lead="Enter your battery specifications to estimate usable capacity and runtime. Battery performance varies by chemistry, temperature, age, discharge rate, manufacturer specifications and system configuration."
      >
        <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <SelectInput label="Battery chemistry" testid="bat-chemistry" value={form.chemistry} onChange={set("chemistry")}
              options={[{ value: "lithium", label: "Lithium / LiFePO4" }, { value: "leadacid", label: "Lead-acid / AGM / Gel" }]} />
            <NumInput label="Voltage per battery" suffix="V" min={1} max={100} testid="bat-voltage" value={form.voltage} onChange={set("voltage")} />
            <NumInput label="Capacity per battery" suffix="Ah" min={1} max={2000} testid="bat-ah" value={form.amp_hours} onChange={set("amp_hours")} />
            <NumInput label="Number of batteries" min={1} max={100} step={1} testid="bat-count" value={form.count} onChange={set("count")} />
            <NumInput label="Battery age" suffix="years" min={0} max={30} testid="bat-age" value={form.age_years} onChange={set("age_years")} />
            <NumInput label="Current state of charge" suffix="%" min={0} max={100} testid="bat-soc" value={form.state_of_charge_pct} onChange={set("state_of_charge_pct")} />
            <NumInput label="Load you want to run" suffix="W" min={0} testid="bat-load" value={form.load_w} onChange={set("load_w")} />
            <NumInput label="Desired runtime (optional)" suffix="h" min={0} testid="bat-runtime" value={form.desired_runtime_h} onChange={set("desired_runtime_h")} />
          </div>
          {error && <div className="mt-5"><ErrorBox msg={error} /></div>}
          <button onClick={compute} disabled={busy} data-testid="bat-compute"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
            {busy ? "Calculating…" : "Calculate"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {result && (
          <div className="mt-10 space-y-8" data-testid="battery-results">
            <ResultGrid items={[
              { label: "Nominal capacity", value: `${result.nominal_wh.toLocaleString()} Wh` },
              { label: "Estimated usable now", value: `${result.available_kwh} kWh`, hint: `Health factor ≈ ${result.health_factor}% · DoD and charge applied` },
              ...(result.est_runtime_h ? [{ label: "Estimated runtime", value: `${result.est_runtime_h} h`, hint: `At ${form.load_w} W load` }] : []),
            ]} />
            {result.notes.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 space-y-2">
                {result.notes.map((n, i) => <p key={i} className="text-sm text-amber-800">{n}</p>)}
              </div>
            )}
            {form.desired_runtime_h && result.est_runtime_h && result.est_runtime_h < form.desired_runtime_h && (
              <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                Your bank delivers ~{result.est_runtime_h} h but you want {form.desired_runtime_h} h — consider adding capacity or reducing load. BNM can size the right upgrade.
              </p>
            )}
            <EstimateNote />
            <WhatsAppCTA message={`Hello BNM. I used the Battery Health Calculator: my bank is about ${result.available_kwh} kWh usable with ~${result.est_runtime_h ?? "?"} h runtime. I would like advice.`} label="Discuss my battery on WhatsApp" />
          </div>
        )}
      </ToolShell>
    </>
  );
}
